import { useMemo } from "react";
import BigNumber from "bignumber.js";
import type { Environment } from "../../../types";
import { useTokenBalance } from "./use-token-balance";
import { useTokenStats, buildStatsKey } from "./use-token-stats";
import { formatTokenValueForDisplay } from "../../../lib/format";

export interface TotalValueResult {
  /** BigNumber 원시 문자열 (예: "1234.56789") */
  totalUsd: string;
  /** 포맷된 값 (예: "1,234.56") */
  displayValue: string;
  /** true면 UI에서 앞에 "≤" 표기 필요 */
  isLowerBound: boolean;
  isLoading: boolean;
}

/**
 * 지갑 보유 토큰(useTokenBalance)들의 USD 합계를 계산.
 * cross-wallet-web-monorepo의 useTotalValue와 동일 정책:
 *  total = Σ (amount × price)
 */
export function useTotalValue(
  env: Environment,
  walletAddress: string,
  enabled: boolean,
): TotalValueResult {
  const { tokens, isLoading: isBalanceLoading } = useTokenBalance(
    env,
    walletAddress,
    enabled,
  );
  const { statsMap } = useTokenStats(env, enabled);

  return useMemo(() => {
    let total = new BigNumber(0);
    for (const t of tokens) {
      const stat = statsMap.get(buildStatsKey(t.chainId, t.address));
      if (!stat) continue;
      const amount = new BigNumber(t.quantity.numeric).shiftedBy(
        -t.quantity.decimals,
      );
      const value = amount.multipliedBy(new BigNumber(stat.price));
      if (value.isNaN()) continue;
      total = total.plus(value);
    }

    const totalUsd = total.toFixed();
    const { displayValue, isLowerBound } = formatTokenValueForDisplay(totalUsd);

    return {
      totalUsd,
      displayValue,
      isLowerBound,
      isLoading: isBalanceLoading,
    };
  }, [tokens, statsMap, isBalanceLoading]);
}
